import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { doc, setDoc, updateDoc, writeBatch, deleteField, FieldPath } from 'firebase/firestore';
import { db } from '../firebase';

// Post-game Scout nudge — the client half of the push-notification loop.
//
// Game dates are timezone-naive local strings ("2026-07-04T18:00"), so the
// server can't compute "3 hours after first pitch" on its own. The device can:
// we stamp each upcoming game with the exact UTC moment its nudge should fire
// (`scoutNudgeAt`), and the sendScoutNudges Cloud Function simply sends
// whatever is due. Tokens live on users/{uid}.fcmTokens so the function can
// reach every coach on the team.
//
// Everything here is best-effort: a nudge is never worth breaking the app for.

const NUDGE_DELAY_MS = 3 * 60 * 60 * 1000;
const QUIET_START_HOUR = 21; // 9pm — nudges landing after this wait for morning
const QUIET_END_HOUR = 9; //    9am — ...and fire here
const MAX_STAMPS_PER_PASS = 100; // stay far under Firestore's 500-write batch cap

let regListener: { remove: () => void } | null = null;
let currentUid: string | null = null;
let currentToken: string | null = null;

/**
 * Register this device for push and save its FCM token to users/{uid}.
 * Silent unless notification permission is already granted — the permission
 * prompt belongs to the pre-game reminders flow (first schedulable game), so
 * this is safe to re-call on every app open and after every reminder pass.
 */
export async function initPushRegistration(uid: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    currentUid = uid;
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') return;

    if (!regListener) {
      regListener = await PushNotifications.addListener('registration', (t) => {
        // AppDelegate bridges APNs → FCM, so t.value is an FCM token that
        // admin.messaging() can send to directly.
        currentToken = t.value;
        const uidNow = currentUid;
        if (!uidNow || !t.value) return;
        setDoc(
          doc(db, 'users', uidNow),
          { fcmTokens: { [t.value]: { platform: 'ios', updatedAt: new Date().toISOString() } } },
          { merge: true },
        ).catch(() => {});
      });
    }
    await PushNotifications.register();
  } catch {
    /* best-effort */
  }
}

/**
 * Best-effort logout hygiene: drop this device's token from the account so a
 * shared iPad doesn't keep nudging the coach who signed out. Fire-and-forget —
 * never let it delay or block the actual sign-out.
 */
export async function clearPushRegistration(): Promise<void> {
  const uid = currentUid;
  const token = currentToken;
  currentUid = null;
  if (!Capacitor.isNativePlatform() || !uid || !token) return;
  try {
    // FieldPath keeps arbitrary token characters out of dot-path parsing.
    await updateDoc(doc(db, 'users', uid), new FieldPath('fcmTokens', token), deleteField());
  } catch {
    /* best-effort */
  }
}

/** Notification-tap listener, filtered to Scout nudges. Null on web. */
export async function addScoutNudgeTapListener(
  onTap: (data: { teamId?: string; gameId?: string }) => void,
): Promise<{ remove: () => void } | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    return await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data: any = action?.notification?.data || {};
      if (data.type === 'scout-nudge') onTap(data);
    });
  } catch {
    return null;
  }
}

/** First pitch + 3h, with anything landing 9pm–9am pushed to 9am next morning. */
function scoutNudgeTimeFor(gameDate: Date): Date {
  const fireAt = new Date(gameDate.getTime() + NUDGE_DELAY_MS);
  const hour = fireAt.getHours();
  if (hour >= QUIET_START_HOUR) {
    fireAt.setDate(fireAt.getDate() + 1);
    fireAt.setHours(QUIET_END_HOUR, 0, 0, 0);
  } else if (hour < QUIET_END_HOUR) {
    fireAt.setHours(QUIET_END_HOUR, 0, 0, 0);
  }
  return fireAt;
}

/**
 * Stamp upcoming games with their nudge fire time. Runs on every platform
 * (whichever coach's device gets there first wins — the value is deterministic
 * from the game date, so devices never fight). Rides the games onSnapshot,
 * like reconcileGameReminders. `scoutNudgeForDate` records the game date the
 * stamp was computed from, so rescheduled games get restamped.
 */
export async function reconcileScoutNudgeTimes(games: any[], team: any): Promise<void> {
  if (!team?.id || team.isDemoSession) return;
  try {
    const now = Date.now();
    const updates: { id: string; fields: Record<string, any> }[] = [];
    for (const g of games || []) {
      if (!g?.id || !g?.date) continue;
      if (g.scoutNudgeAt && g.scoutNudgeForDate === g.date) continue; // already current
      if (g.stats && Object.keys(g.stats).length > 0) continue; // scorebook already imported
      const gameDate = new Date(g.date);
      if (isNaN(gameDate.getTime())) continue;
      const fireAt = scoutNudgeTimeFor(gameDate);
      if (fireAt.getTime() <= now) {
        // Never stamp a moment already in the past — at feature-ship (or when a
        // coach back-dates a game) that would nudge for ancient games. If a
        // date edit left a stale future stamp behind, clear it instead.
        if (g.scoutNudgeAt && g.scoutNudgeForDate !== g.date) {
          updates.push({ id: g.id, fields: { scoutNudgeAt: deleteField(), scoutNudgeForDate: deleteField() } });
        }
        continue;
      }
      updates.push({
        id: g.id,
        fields: { scoutNudgeAt: fireAt.toISOString(), scoutNudgeForDate: g.date },
      });
      if (updates.length >= MAX_STAMPS_PER_PASS) break;
    }
    if (updates.length === 0) return;
    const batch = writeBatch(db);
    for (const u of updates) {
      batch.update(doc(db, `teams/${team.id}/games`, u.id), u.fields);
    }
    await batch.commit();
  } catch {
    /* best-effort — the server only ever sends what was successfully stamped */
  }
}
